import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Users, Target, BarChart3 } from 'lucide-react';

interface ConversionMetric {
  id: string;
  name: string;
  value: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  status: 'healthy' | 'warning' | 'critical';
  description: string;
}

export const ConversionMetricsView: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  
  const metrics: ConversionMetric[] = [
    {
      id: 'health',
      name: 'Project Health Score',
      value: 87,
      change: 5,
      trend: 'up',
      status: 'healthy',
      description: 'Overall project health based on task completion, timeline adherence, and client engagement'
    },
    {
      id: 'engagement',
      name: 'Client Engagement',
      value: 72,
      change: -3,
      trend: 'down',
      status: 'warning',
      description: 'Client login frequency, task interaction, and response time to communications'
    },
    {
      id: 'dropoff',
      name: 'Drop-off Risk',
      value: 23,
      change: -8,
      trend: 'down',
      status: 'healthy',
      description: 'Likelihood of client disengagement based on activity patterns and milestone delays'
    },
    {
      id: 'ttv',
      name: 'Time-to-Value',
      value: 42,
      change: -5,
      trend: 'down',
      status: 'healthy',
      description: 'Days until client realizes first significant value from the implementation'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20';
      case 'warning': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'critical': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-[#8A9150]" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <BarChart3 className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4">
            Conversion Analytics
          </h2>
          <p className="text-gray-600 text-lg">
            Track project health, client engagement, and success metrics
          </p>
        </div>

        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Time Range:</span>
            <div className="flex items-center space-x-2">
              {[
                { key: '7d', label: '7 Days' },
                { key: '30d', label: '30 Days' },
                { key: '90d', label: '90 Days' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTimeRange(key as any)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    timeRange === key
                      ? 'bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white shadow-lg shadow-[#4267A1]/25'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric) => (
            <motion.div
              key={metric.id}
              whileHover={{ scale: 1.02 }}
              className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(metric.status)}`}>
                  {metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                </div>
                {getTrendIcon(metric.trend)}
              </div>
              
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{metric.name}</h3>
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold text-[#4267A1]">
                    {metric.value}{metric.id === 'ttv' ? 'd' : metric.id === 'health' || metric.id === 'engagement' || metric.id === 'dropoff' ? '%' : ''}
                  </span>
                  <span className={`text-sm font-medium ${
                    metric.change > 0 ? 'text-[#8A9150]' : metric.change < 0 ? 'text-red-500' : 'text-gray-500'
                  }`}>
                    {metric.change > 0 ? '+' : ''}{metric.change}{metric.id === 'ttv' ? 'd' : '%'}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 leading-relaxed">{metric.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Users className="w-6 h-6 text-[#4267A1] mr-3" />
              Engagement Trends
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Daily Logins</p>
                  <p className="text-sm text-gray-600">Client portal visits tracked</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#4267A1]">3.2</p>
                  <p className="text-sm text-[#8A9150]">+12%</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Task Activity</p>
                  <p className="text-sm text-gray-600">Tasks viewed, updated, completed</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#4267A1]">8.7</p>
                  <p className="text-sm text-red-500">-5%</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Response Lag</p>
                  <p className="text-sm text-gray-600">Average reply time to messages</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#4267A1]">4.2h</p>
                  <p className="text-sm text-[#8A9150]">-18%</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Target className="w-6 h-6 text-[#4267A1] mr-3" />
              Success Indicators
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#8A9150]/10 to-[#A5B366]/5 rounded-lg border border-[#8A9150]/20">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#8A9150]" />
                  <div>
                    <p className="font-medium text-gray-900">Milestone Completion</p>
                    <p className="text-sm text-gray-600">On-time delivery rate</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-[#8A9150]">94%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-100/50 to-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium text-gray-900">Timeline Adherence</p>
                    <p className="text-sm text-gray-600">Projects on schedule</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-yellow-600">78%</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-100/50 to-red-50 rounded-lg border border-red-200">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-gray-900">Risk Factors</p>
                    <p className="text-sm text-gray-600">Active concerns</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-red-600">3</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
